import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

// PrimeNG
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { SuperAdminService } from '../../../services/super-admin/super-admin.service';
import { YoutubeIntegrationStatusResponse } from '../../../models/youtube/youtubeIntegrationStatusResponse';
import { environment } from '../../../../environments/environment';
import { YoutubeIntegrationRequest } from '../../../models/youtube/youtubeIntegrationRequest';

@Component({
  selector: 'app-youtube-integration',
  standalone: true,
  imports: [
    CommonModule, CardModule, ButtonModule, TagModule, ToastModule, ProgressSpinnerModule, ConfirmDialogModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './youtube-integration.component.html',
  styleUrl: './youtube-integration.component.scss'
})


export class YoutubeIntegrationComponent implements OnInit {
  private service = inject(SuperAdminService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  // Configuration (Should match Google Console)
  private readonly GOOGLE_CLIENT_ID = environment.google.clientId; 
  private readonly REDIRECT_URI = window.location.origin + window.location.pathname; 

  // State
  status = signal<YoutubeIntegrationStatusResponse>({ isConnected: false });
  loading = signal<boolean>(true);
  isProcessingCode = signal<boolean>(false);

  ngOnInit() {
    // 1. Check if we are returning from Google (OAuth Callback)
    const code = this.route.snapshot.queryParams['code'];
    const error = this.route.snapshot.queryParams['error'];
    if (code) {
      this.handleAuthCallback(code);
    } else if (error) {
      this.messageService.add({ severity: 'error', summary: 'OAuth Error', detail: 'Google permission denied.' });
      this.loadStatus();
    } else {
      this.loadStatus();
    }
  }

  loadStatus() {
    this.loading.set(true);
    this.service.getYouTubeIntegrationStatus().subscribe({
      next: (res) => {
        this.status.set(res);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  // --- Step 1: Start Integration ---
  initiateIntegration() {
    // Construct Google OAuth URL
    const params = new URLSearchParams({
      client_id: this.GOOGLE_CLIENT_ID,
      redirect_uri: this.REDIRECT_URI,
      response_type: 'code',
      scope: 'https://www.googleapis.com/auth/youtube.readonly',
      access_type: 'offline', // CRITICAL for Refresh Token
      prompt: 'consent'       // CRITICAL to force refresh token generation
    });

    // Redirect user
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  // --- Step 2: Handle Callback (Code -> Backend) ---
  handleAuthCallback(code: string) {
    this.isProcessingCode.set(true);
    
    // Clean URL immediately so code isn't visible/reused
    this.router.navigate([], { replaceUrl: true, queryParams: {} });

    const request: YoutubeIntegrationRequest = {
      code: code,
      redirectUri: this.REDIRECT_URI
    };

    this.service.activateYouTubeIntegration(request).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Connected', detail: 'YouTube Integration Successful!' });
        this.isProcessingCode.set(false);
        this.loadStatus(); // Refresh UI
      },
      error: (err) => {
        this.isProcessingCode.set(false);
        this.messageService.add({ severity: 'error', summary: 'Integration Failed', detail: 'Could not exchange token.' });
        this.loadStatus();
      }
    });
  }

  // --- Step 3: Disconnect ---
  deintegrate() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to de-activate? This will stop syncing videos and the Vlogs page will stop working.',
      header: 'De-activate Confirmation',
      icon: 'pi pi-exclamation-circle',
      acceptLabel: 'Deactivate',
      rejectLabel: 'Cancel',
      acceptIcon: "none",
      rejectIcon: "none",
      rejectButtonStyleClass: "p-button-text p-button-secondary font-bold",
      acceptButtonStyleClass: "p-button-danger font-bold", // Red button for destructive action
      
      accept: () => {
        // ACTUAL LOGIC MOVED HERE
        this.loading.set(true);
        this.service.deactivateYouTubeIntegration().subscribe({ 
          next: () => {
            this.messageService.add({ severity: 'info', summary: 'Disconnected', detail: 'Channel removed' });
            this.loadStatus();
          },
          error: () => {
            this.loading.set(false);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not disconnect.' });
          }
        });
      },
      reject: () => {
         // Optional: Do nothing on cancel
      }
    });
  }
}